import { createSignal, createContext, useContext, ParentComponent, createMemo } from 'solid-js';
import * as i18n from '@solid-primitives/i18n';
import plTranslations from './locales/pl';
import enTranslations from './locales/en';

// Available locales
export const LOCALES = {
  pl: 'pl',
  en: 'en'
} as const;

export type Locale = keyof typeof LOCALES;

// Flatten nested dictionaries
const flatPl = i18n.flatten(plTranslations);
const flatEn = i18n.flatten(enTranslations);

type FlatDict = typeof flatPl;

// Storage key for persisting locale
const LOCALE_STORAGE_KEY = 'gra-miejska-locale';

// Get initial locale from storage or browser
function getInitialLocale(): Locale {
  // Check localStorage first
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && (stored === 'pl' || stored === 'en')) {
      return stored as Locale;
    }

    // Check browser language
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'en') {
      return 'en';
    }
  }

  // Default to Polish
  return 'pl';
}

// Locale context for managing current locale
interface LocaleContextValue {
  locale: () => Locale;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LocaleContext = createContext<LocaleContextValue>();

// Provider component
export const I18nProvider: ParentComponent = (props) => {
  const [locale, setLocaleSignal] = createSignal<Locale>(getInitialLocale());

  // Create reactive dictionary accessor
  const dict = createMemo<FlatDict>(() => {
    return locale() === 'pl' ? flatPl : flatEn;
  });

  // Create translator
  const translator = i18n.translator(() => dict(), i18n.resolveTemplate);

  const setLocale = (newLocale: Locale) => {
    setLocaleSignal(newLocale);

    // Persist to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    }
  };

  const toggleLocale = () => {
    const newLocale = locale() === 'pl' ? 'en' : 'pl';
    setLocale(newLocale);
  };

  // Translation function with parameter support
  const t = (key: string, params?: Record<string, string | number>): string => {
    try {
      const translation = translator(key as keyof FlatDict, params as any);
      if (translation === undefined || translation === null) {
        console.warn(`Missing translation for key: ${key}`);
        return key;
      }
      return String(translation);
    } catch {
      console.warn(`Translation error for key: ${key}`);
      return key;
    }
  };

  const localeValue: LocaleContextValue = {
    locale,
    setLocale,
    toggleLocale,
    t
  };

  return (
    <LocaleContext.Provider value={localeValue}>
      {props.children}
    </LocaleContext.Provider>
  );
};

// Hook to use translations
export function useTranslation() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useTranslation must be used within I18nProvider');
  }

  return { t: context.t, locale: context.locale };
}

// Hook to manage locale
export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within I18nProvider');
  }
  return context;
}

// Export types
export type { TranslationKeys } from './locales/pl';
